import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);


// Interactive Health Story Section.
class InteractiveHealthStorySection {
  constructor(el) {
    this.sectionEl = el;

    if (!this.sectionEl) {
      return;
    }

    this.allImages = this.sectionEl.querySelectorAll(
      '.interactive-health-story-section__image',
    );
    this.steps = this.sectionEl.querySelectorAll(
      '.interactive-health-story-section__step',
    );

    if (0 === this.steps.length) {
      return;
    }

    this.init();
  }

  /**
   * Show a specific image by type and index, hiding all others.
   *
   * @param {string} type  - 'heading' or 'story'
   * @param {number} index - Section index (0-based)
   */
  showImage(type, index) {
    const target = this.sectionEl.querySelector(
      `.interactive-health-story-section__image[data-type="${type}"][data-index="${index}"]`,
    );

    if (!target) {
      return;
    }

    // Already the active image — nothing to do.
    if (target.classList.contains('is-active')) {
      return;
    }

    const currentActive = this.sectionEl.querySelector(
      '.interactive-health-story-section__image.is-active',
    );

    // When heading and story share the same photo, a CSS opacity crossfade
    // would cause a visible dip through the background even with z-index
    // stacking, because the incoming image is transparent mid-transition.
    // Since both images are visually identical, skip the fade entirely.
    const isSameSrc = currentActive && currentActive.src === target.src;

    if (isSameSrc) {
      // Disable transitions, do an instant swap, then restore.
      this.allImages.forEach(img => {
        img.style.transition = 'none';
        img.classList.remove('is-active');
      });
      target.style.transition = 'none';
      target.classList.add('is-active');
      // Force a style flush before re-enabling so the browser applies the
      // transition:none before the next paint.
      target.getBoundingClientRect();
      this.allImages.forEach(img => {
        img.style.transition = '';
      });
    } else {
      this.allImages.forEach(img => {
        img.classList.remove('is-active');
      });
      target.classList.add('is-active');
    }
  }


  init() {
    const headerOffset = this.getHeaderOffset();
    const viewportHeight = window.innerHeight;
    const availableHeight = viewportHeight - headerOffset;

    const imageStickyEl = this.sectionEl.querySelector(
      '.interactive-health-story-section__image-sticky',
    );

    // Compute it on demand so ScrollTrigger can re-evaluate it on every refresh, instead of freezing a wrong value
    // measured before the image had dimensions.
    const getImageTop = () => {
      if (imageStickyEl) {
        const activeImg = imageStickyEl.querySelector('.is-active');
        if (activeImg) {
          const stickyRect = imageStickyEl.getBoundingClientRect();
          const imgRect = activeImg.getBoundingClientRect();
          return headerOffset + (imgRect.top - stickyRect.top);
        }
      }
      return headerOffset + availableHeight * 0.1;
    };

    this.steps.forEach((step, idx) => {
      const sectionIndex = parseInt(
        step.getAttribute('data-section-index'),
        10,
      );
      const headingStep = step.querySelector(
        '.interactive-health-story-section__heading-step',
      );
      const heading = step.querySelector(
        '.interactive-health-story-section__heading',
      );
      const textStep = step.querySelector(
        '.interactive-health-story-section__text-step',
      );
      const storyText = step.querySelector(
        '.interactive-health-story-section__story-text',
      );

      if (!heading || !textStep || !headingStep || !storyText) {
        return;
      }

      const headingHeight = heading.offsetHeight;
      const stickyTop =
        headerOffset + (availableHeight - headingHeight) / 2;
        
      const riseRoom = viewportHeight - stickyTop;
      let topGap = riseRoom;
      if (0 < idx) {
        const prevText = this.steps[idx - 1].querySelector(
          '.interactive-health-story-section__text-step',
        );
        const prevPadBottom = prevText
          ? parseFloat(window.getComputedStyle(prevText).paddingBottom) || 0
          : 0;
        topGap = riseRoom - prevPadBottom;
      }
      step.style.marginTop = `${Math.max(0, topGap)}px`;

      // Phase 1 — heading sticks at viewport center while story text rises.
      heading.style.position = 'sticky';
      heading.style.top = `${stickyTop}px`;
      heading.style.opacity = 1;
      headingStep.style.paddingTop = `${stickyTop}px`;

      // Phase 2 — story image swap when the story text first appears at
      // the bottom of the window.
      ScrollTrigger.create({
        trigger: storyText,
        start: 'top bottom',
        onEnter: () => this.showImage('story', sectionIndex),
        onLeaveBack: () => this.showImage('heading', sectionIndex),
      });

      // Phase 3 — swap to NEXT section's heading image when this section's
      // content ENDS: when the bottom of the links (or, if absent, the
      // story text) reaches the top of the sticky image.
      const nextStep = this.steps[idx + 1];
      if (nextStep) {
        const nextSectionIndex = parseInt(
          nextStep.getAttribute('data-section-index'),
          10,
        );
        const links = step.querySelector(
          '.interactive-health-story-section__links',
        );
        const linksHeading = step.querySelector(
          '.interactive-health-story-section__links-heading',
        );
        const contentEnd = links || linksHeading || storyText;
        ScrollTrigger.create({
          trigger: contentEnd,
          // Function start + invalidateOnRefresh so the image-top offset is
          // recomputed on every refresh, instead of being frozen at init.
          start: () => `bottom ${getImageTop()}px`,
          invalidateOnRefresh: true,
          onEnter: () =>
            this.showImage('heading', nextSectionIndex),
          onLeaveBack: () => this.showImage('story', sectionIndex),
        });
      }
    });

    // Set initial runway for the last step before triggers are created.
    this.setLastStepRunway();

    // Recompute once everything has loaded so the swaps line up.
    const refresh = () => {
      this.setLastStepRunway();
      ScrollTrigger.refresh();
    };
    window.addEventListener('load', refresh);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refresh);
    }
    // Recompute on resize (orientation change, browser chrome show/hide, etc.).
    let _resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(refresh, 150);
    });
    if (imageStickyEl) {
      imageStickyEl
        .querySelectorAll('.interactive-health-story-section__image')
        .forEach(img => {
          if (!img.complete) {
            img.addEventListener('load', refresh, { once: true });
          }
        });
    }
  }

  /**
   * Compute and apply the exact bottom padding the final step needs so the
   * sticky image stays locked until the story text has scrolled to the image's
   * vertical centre, regardless of paragraph length or viewport height.
   *
   * Applied to `__text-step` (where `getComputedStyle` padding is already
   * read by the topGap calculation) so the geometry stays consistent.
   */
  setLastStepRunway() {
    const lastStep = this.steps[this.steps.length - 1];
    if (!lastStep) {
      return;
    }
    const lastTextStep = lastStep.querySelector(
      '.interactive-health-story-section__text-step',
    );
    if (!lastTextStep) {
      return;
    }

    const imageStickyEl = this.sectionEl.querySelector(
      '.interactive-health-story-section__image-sticky',
    );
    const headerOffset = this.getHeaderOffset();
    const availableHeight =
      imageStickyEl && 0 < imageStickyEl.offsetHeight
        ? imageStickyEl.offsetHeight
        : window.innerHeight - headerOffset;

    if (0 >= availableHeight) {
      return;
    }

    const storyText = lastTextStep.querySelector(
      '.interactive-health-story-section__story-text',
    );
    const links = lastTextStep.querySelector(
      '.interactive-health-story-section__links',
    );
    const linksHeading = lastTextStep.querySelector(
      '.interactive-health-story-section__links-heading',
    );
    const contentEnd = links || linksHeading || storyText;

    let contentHeight = 0;
    if (storyText && contentEnd) {
      const startRect = storyText.getBoundingClientRect();
      const endRect = contentEnd.getBoundingClientRect();
      const endMarginBottom =
        parseFloat(window.getComputedStyle(contentEnd).marginBottom) || 0;
      contentHeight = Math.max(
        0,
        endRect.bottom - startRect.top + endMarginBottom,
      );
    } else {
      contentHeight = lastTextStep.offsetHeight;
    }

    // Retrieve the bottom padding applied to the image in the sticky container.
    const stickyStyles = imageStickyEl
      ? window.getComputedStyle(imageStickyEl)
      : null;
    const stickyPaddingBottom = stickyStyles
      ? parseFloat(stickyStyles.paddingBottom) || 0
      : 0;

    const activeImg = imageStickyEl
      ? imageStickyEl.querySelector(
        '.interactive-health-story-section__image.is-active',
      ) ||
        imageStickyEl.querySelector('.interactive-health-story-section__image')
      : null;

    let imgBottomGap = stickyPaddingBottom;
    if (activeImg && 0 < activeImg.offsetHeight && imageStickyEl) {
      const stickyRect = imageStickyEl.getBoundingClientRect();
      const imgRect = activeImg.getBoundingClientRect();
      imgBottomGap = Math.max(
        stickyPaddingBottom,
        Math.round(stickyRect.bottom - imgRect.bottom),
      );
    }

    // Align the vertical centre of the left text section with the sticky image
    // when space permits, but ensure padding-bottom never drops below the bottom
    // padding applied to the image (interactive-health-story-section__image-sticky).
    const centerRunway = Math.round((availableHeight - contentHeight) / 2);
    const runway = Math.max(imgBottomGap, centerRunway);
    lastTextStep.style.paddingBottom = `${runway}px`;
  }


  /**
   * Get the nav header height for offset calculations.
   *
   * @returns {number} Header height in pixels.
   */
  getHeaderOffset() {
    const mainHeader = document.querySelector('.main-header');
    return mainHeader ? mainHeader.offsetHeight : 70;
  }
}

// Mobile overlay story section.
class InteractiveHealthStorySectionMobile {
  constructor(el) {
    this.sectionEl = el;

    if (!this.sectionEl) {
      return;
    }

    this.allImages = this.sectionEl.querySelectorAll(
      '.interactive-health-story-section-mobile__stage-image',
    );
    this.sections = this.sectionEl.querySelectorAll(
      '.interactive-health-story-section-mobile__section',
    );

    if (0 === this.allImages.length || 0 === this.sections.length) {
      return;
    }

    this.media = gsap.matchMedia();
    this.media.add('(max-width: 599px)', () => {
      this.init();

      return () => {
        this.reset();
      };
    });
  }

  /**
   * Show a mobile stage image by type and index.
   *
   * @param {string} type  - 'heading' or 'story'
   * @param {number} index - Section index (0-based)
   */
  showImage(type, index) {
    const target =
      this.findImage(type, index) ||
      this.findImage('heading', index) ||
      this.findImage('story', index);

    if (!target) {
      return;
    }

    // Already the active image — nothing to do.
    if (target.classList.contains('is-active')) {
      return;
    }

    const currentActive = this.sectionEl.querySelector(
      '.interactive-health-story-section-mobile__stage-image.is-active',
    );

    // Same-src instant swap to prevent dip-to-white on identical images.
    const isSameSrc = currentActive && currentActive.src === target.src;

    if (isSameSrc) {
      this.allImages.forEach(img => {
        img.style.transition = 'none';
        img.classList.remove('is-active');
      });
      target.style.transition = 'none';
      target.classList.add('is-active');
      target.getBoundingClientRect();
      this.allImages.forEach(img => {
        img.style.transition = '';
      });
    } else {
      this.allImages.forEach(img => {
        img.classList.remove('is-active');
      });
      target.classList.add('is-active');
    }
  }

  /**
   * Find a mobile stage image.
   *
   * @param {string} type  - 'heading' or 'story'
   * @param {number} index - Section index (0-based)
   * @returns {Element|null} Matching image.
   */
  findImage(type, index) {
    return this.sectionEl.querySelector(
      `.interactive-health-story-section-mobile__stage-image[data-type="${type}"][data-index="${index}"]`,
    );
  }

  init() {
    this.showImage('heading', 0);

    this.sections.forEach(section => {
      const sectionIndex = parseInt(
        section.getAttribute('data-section-index'),
        10,
      );
      const headingPanel = section.querySelector(
        '.interactive-health-story-section-mobile__panel--heading',
      );
      const storyPanel = section.querySelector(
        '.interactive-health-story-section-mobile__panel--story',
      );
      const headingContent = headingPanel
        ? headingPanel.querySelector(
          '.interactive-health-story-section-mobile__text-card--heading',
        ) || headingPanel
        : null;

      if (headingContent) {
        ScrollTrigger.create({
          trigger: headingContent,
          start: 'top bottom',
          end: 'bottom top',
          onEnter: () => this.showImage('heading', sectionIndex),
          onEnterBack: () => this.showImage('heading', sectionIndex),
        });
      }

      if (headingContent) {
        ScrollTrigger.create({
          trigger: headingContent,
          start: 'bottom top',
          onEnter: () => this.showImage('story', sectionIndex),
          onLeaveBack: () => this.showImage('heading', sectionIndex),
        });
      }

      if (storyPanel) {
        ScrollTrigger.create({
          trigger: storyPanel,
          start: 'top top',
          end: 'bottom top',
          onEnter: () => this.showImage('story', sectionIndex),
          onEnterBack: () => this.showImage('story', sectionIndex),
          onLeaveBack: () => this.showImage('heading', sectionIndex),
        });
      }
    });
  }

  reset() {
    this.showImage('heading', 0);
  }
}

// Initialize when DOM is ready.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.interactive-health-story-section').forEach(el => {
    new InteractiveHealthStorySection(el);
  });
  document
    .querySelectorAll('.interactive-health-story-section-mobile')
    .forEach(el => {
      new InteractiveHealthStorySectionMobile(el);
    });
});
